import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { pixelEnabled, readConsent, saveConsent, trackPixel, type Consent } from '../analytics/metaPixel';
import { PRIVACY_URL } from '@/config/legal';
import { Button } from '../ui';

/**
 * Asks once, on either site, whether Meta may measure ads — and sends a PageView per
 * route once it may.
 *
 * Someone who answered on saddle-sites.com is never asked here: the answer is a cookie on
 * the shared domain (see analytics/metaPixel.ts). People tapping an ad that points
 * straight at the builder never saw the landing banner, which is why this one exists.
 */
export default function ConsentBanner() {
  const enabled = pixelEnabled();
  const [choice, setChoice] = useState<Consent | null>(() => (enabled ? readConsent() : null));
  const { pathname } = useLocation();
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    if (choice !== 'granted' || lastPath.current === pathname) return;
    lastPath.current = pathname;
    trackPixel('PageView');
  }, [choice, pathname]);

  if (!enabled || choice) return null;

  const decide = (c: Consent) => {
    saveConsent(c);
    setChoice(c);
  };

  return (
    <div className="consent card clay-card" role="region" aria-label="Cookie consent">
      <p className="type-caption">
        We use Meta&rsquo;s cookies to see which ads bring people here. Nothing loads unless you
        say yes.{' '}
        <a href={PRIVACY_URL} target="_blank" rel="noreferrer">Privacy</a>
      </p>
      <div className="consent-actions">
        <Button title="No thanks" variant="secondary" onPress={() => decide('denied')} />
        <Button title="Accept" onPress={() => decide('granted')} />
      </div>
    </div>
  );
}
